import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI, setStoredToken, getStoredToken } from '../utils/api';
import { Phone, Shield, ArrowRight, X } from 'lucide-react';
import { toast } from 'sonner';
import OtpResendButton from './OtpResendButton';

const PhoneVerifyGate = ({ onVerified, onClose }) => {
  const { user, checkAuth } = useAuth();
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('phone');
  const [loading, setLoading] = useState(false);

  const handleSendOTP = async () => {
    if (phone.length < 10) { toast.error('Enter a valid 10-digit number'); return; }
    setLoading(true);
    try {
      await authAPI.sendOTP(`+91${phone}`);
      setStep('otp');
      toast.success('OTP sent!');
    } catch (e) {
      setStep('otp');
      toast.info('Test mode — enter 123456');
    } finally { setLoading(false); }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) { toast.error('Enter 6-digit OTP'); return; }
    setLoading(true);
    try {
      const r = await authAPI.verifyOTP(`+91${phone}`, otp, user?.name, user?.email);
      // Only overwrite token if a NEW one came back (fresh-login flow). In the
      // account-linking flow (user already authenticated), the backend reuses
      // the existing session and does not return session_token — keep our token.
      if (r.data?.session_token) setStoredToken(r.data.session_token);
      const t = getStoredToken();
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/update-profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({ phone: `+91${phone}`, phone_verified: true })
      });
      await checkAuth();
      toast.success('Phone verified! You can now post ads.');
      if (onVerified) onVerified();
    } catch (e) {
      toast.error('Invalid OTP');
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4" data-testid="phone-verify-gate">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-[#0A0B10]">Verify Your Phone</h2>
          {onClose && (
            <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 text-[#9CA3AF]"><X size={18} /></button>
          )}
        </div>

        <div className="bg-[#fff8f3] rounded-xl p-3 mb-4 border border-[#ff8500]/10">
          <p className="flex items-center gap-1.5 text-xs font-bold text-[#9A4500]"><Shield size={13} /> Phone verification is required to post ads</p>
          <p className="text-[10px] text-[#6B7280] mt-1">This helps prevent fraud and build trust with buyers</p>
        </div>

        {step === 'phone' && (
          <div className="space-y-3">
            <div className="flex gap-2">
              <span className="flex items-center px-3 bg-[#f0edff] rounded-xl text-sm text-[#1800ad] font-bold">+91</span>
              <input data-testid="gate-phone-input" type="tel" value={phone}
                onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="10-digit number" maxLength={10} autoFocus
                className="flex-1 rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm focus:border-[#1800ad] outline-none" />
            </div>
            <button onClick={handleSendOTP} disabled={loading || phone.length < 10} data-testid="gate-send-otp"
              className="w-full bg-[#1800ad] text-white rounded-xl py-3 font-bold text-sm disabled:opacity-40 flex items-center justify-center gap-2">
              {loading ? 'Sending...' : <><ArrowRight size={15} /> Send OTP</>}
            </button>
          </div>
        )}

        {step === 'otp' && (
          <div className="space-y-3">
            <input data-testid="gate-otp-input" type="text" value={otp}
              onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="6-digit OTP" maxLength={6} autoFocus
              className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-center tracking-[0.4em] font-bold focus:border-[#1800ad] outline-none" />
            <button onClick={handleVerifyOTP} disabled={loading || otp.length < 6} data-testid="gate-verify-otp"
              className="w-full bg-[#1800ad] text-white rounded-xl py-3 font-bold text-sm disabled:opacity-40">
              {loading ? 'Verifying...' : 'Verify OTP'}
            </button>
            {/* iter65 — Resend OTP with 30s countdown */}
            <div className="flex items-center justify-center">
              <OtpResendButton
                onResend={async () => {
                  try {
                    await authAPI.sendOTP(`+91${phone}`);
                    toast.success('OTP resent to your phone');
                  } catch (e) {
                    const detail = e?.response?.data?.detail;
                    toast.error(detail || 'Failed to resend OTP. Try again.');
                    throw e;
                  }
                }}
                testIdPrefix="gate-resend"
              />
            </div>
            <button onClick={() => { setStep('phone'); setOtp(''); }}
              className="w-full text-[#9CA3AF] text-xs hover:text-[#1800ad] py-1">Change number</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhoneVerifyGate;
